import {ChangeEvent, FocusEvent, FormEvent, useState} from 'react';
import styled from 'styled-components';
import {getGqlErrorMessage} from '@/api/request';
import {useUserOpinionCreateOne} from '@/api/user-opinion-report';
import {Button, ButtonGroup} from '@/components/Button';
import {Checkbox, CheckboxItem, CheckboxLabel} from '@/components/Checkbox';
import toast from '@/components/HotToast';
import {Modal} from '@/components/Modal';
import {PostcodeFinder} from '@/components/PostcodeFinder';
import {TextArea} from '@/components/TextArea';
import {TextField} from '@/components/TextField';
import {Typography} from '@/components/Typography';
import {PostcodeData} from '@/interface';
import {Divider, Flexbox, FlexFullGrow, Section} from '@/styled-components';

type FieldTypes = 'addressOpinion' | 'etcOpinion';

interface IProps {
  fields: Array<{
    type: FieldTypes | string;
    name: string;
    hint?: string;
  }>;
  itemId?: string;
  onClose(): void;
}

const isInvalidValue = (value: string) => {
  return !value || value.trim().length < 10;
};

const checkFormValidation = (
  selectedFileds: Array<FieldTypes>,
  values: {[key: string]: any}
) => {
  if (!selectedFileds?.length) return false;

  const emptyFields = selectedFileds.filter((i) =>
    i === 'addressOpinion'
      ? !values.newAddress && isInvalidValue(values[i])
      : isInvalidValue(values[i])
  );

  return !emptyFields.length;
};

export const IssueReportModal = ({fields, onClose}: IProps) => {
  const [selectedFileds, setSelectedFields] = useState<FieldTypes[]>([]);
  const [values, setValues] = useState<{[key: string]: any}>({});
  const [error, setError] = useState<{[key: string]: boolean}>({});

  const disabled = !checkFormValidation(selectedFileds, values);

  const {mutate, isPending} = useUserOpinionCreateOne({
    onSuccess: () => {
      toast('정보 수정 제안을 전송했습니다.');
      onClose();
    },
    onError: (error) => {
      toast.error(getGqlErrorMessage(error));
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    mutate(values);
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const {value, name} = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    const {value, name} = e.target;
    const isEmptyValue = isInvalidValue(value);

    if (name === 'addressOpinion') {
      setError((prev) => ({
        ...prev,
        [name]: !values.newAddress && isEmptyValue,
      }));
    } else {
      setError((prev) => ({
        ...prev,
        [name]: isEmptyValue,
      }));
    }
  };

  const handlePostCode = (data: PostcodeData) => {
    setValues((prev) => ({
      ...prev,
      newAddress: data.address,
    }));
  };

  const onCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const {checked, name} = e.target;
    const type = name as FieldTypes;

    setSelectedFields((prev) => {
      if (checked) {
        return [...prev, type];
      }

      return prev.filter((i) => i !== type);
    });
  };

  return (
    <Modal title="정보 수정 제안" width={400} onClose={onClose}>
      <Root>
        <form onSubmit={handleSubmit}>
          <Box>
            <Typography variant="medium14">
              알고 계신 정보와 다른 정보가 있다면 제안해주세요. 검토 후 실제
              서비스에 반영되기까지는 시일이 다소 소요될 수 있습니다.
            </Typography>
            <Section spacing={4}>
              <Typography variant="medium14">
                제안할 정보의 종류를 선택한 후 내용을 입력해주세요.
              </Typography>
            </Section>
            {fields.map((i) => {
              const checked =
                selectedFileds.findIndex((type) => type === i.type) > -1;

              return (
                <Section spacing={4} key={i.type}>
                  <Flexbox>
                    <CheckboxItem>
                      <Checkbox
                        checked={checked}
                        name={i.type}
                        onChange={onCheck}
                      />
                      <CheckboxLabel>{i.name}</CheckboxLabel>
                    </CheckboxItem>
                  </Flexbox>
                  {i.type === 'addressOpinion' && (
                    <Section spacing={2}>
                      <PostcodeFinder
                        render={
                          <TextField
                            className="cursor-pointer"
                            label="주소"
                            fullWidth
                            readOnlyWithAction={checked}
                            disabled={!checked}
                            labelOptions={{
                              position: 'overlay-top',
                            }}
                            placeholder="제안할 주소를 입력해주세요."
                            value={values.newAddress ?? ''}
                          />
                        }
                        onComplete={handlePostCode}
                      />
                    </Section>
                  )}
                  <Section spacing={2}>
                    <TextArea
                      name={i.type}
                      value={values?.[i.type] ?? ''}
                      showCounter
                      countHint="최소 10글자"
                      placeholder={i.hint}
                      height="102px"
                      maxLength={500}
                      disabled={!checked}
                      error={checked && error[i.type]}
                      onBlur={onBlur}
                      onChange={onChange}
                    />
                  </Section>
                </Section>
              );
            })}
          </Box>
          <Divider />
          <Box>
            <ButtonGroup>
              <Button
                variant="outline"
                size="regular"
                color="black"
                type="button"
                onClick={onClose}
              >
                취소
              </Button>
              <FlexFullGrow />
              <Button
                variant="fill"
                size="regular"
                color="primary"
                disabled={disabled || isPending}
              >
                {isPending ? '전송 중...' : '전송'}
              </Button>
            </ButtonGroup>
          </Box>
        </form>
      </Root>
    </Modal>
  );
};

const Root = styled.div`
  .cursor-pointer {
    cursor: pointer;
  }
`;

const Box = styled.div`
  padding: 16px;
`;
